import { useEffect, useMemo, useState } from 'react';
import * as XLSX from 'xlsx-js-style';
import { Plus, Save, Trash2, Upload, Download, X, AlertTriangle, Search, Filter, XCircle, ChevronUp, ChevronDown, RotateCcw, Loader2 } from 'lucide-react';
import { warehouseService } from '../services/warehouseService';
import { basePriceService } from '../services/basePriceService';
import { WorkiomTablePagination } from '../components/WorkiomTablePagination';
import { exportRowsToExcel } from '../utils/excelExport';

type BasePriceRow = {
  id?: string;
  warehouse_id: string;
  warehouse_name: string;
  delivery_city: string;
  delivery_district: string;
  base_price: number;
  own_assignment_priority: number;
  valid_from: string;
  valid_until?: string;
};

type BasePriceFilterColumn = 'warehouse_name' | 'delivery_city' | 'delivery_district' | 'base_price' | 'own_assignment_priority' | 'valid_from' | 'valid_until';
type BasePriceFilterOperator = 'contains' | 'notContains' | 'equals' | 'notEquals' | 'empty' | 'notEmpty' | 'dateEquals' | 'dateOnOrAfter' | 'dateOnOrBefore' | 'dateRange' | 'dateToday' | 'dateThisMonth';
type BasePriceFilterRule = { id: string; join: 'and' | 'or'; column: BasePriceFilterColumn; operator: BasePriceFilterOperator; value: string };

const BASE_PRICE_FILTER_COLUMNS: Array<{ key: BasePriceFilterColumn; label: string; date?: boolean }> = [
  { key: 'warehouse_name', label: 'Depo' },
  { key: 'delivery_city', label: 'Teslim İl' },
  { key: 'delivery_district', label: 'Teslim İlçe' },
  { key: 'base_price', label: 'Baz Fiyat' },
  { key: 'own_assignment_priority', label: 'Özmal Atama Önceliği' },
  { key: 'valid_from', label: 'Geçerlilik Tarihi', date: true },
  { key: 'valid_until', label: 'Geçerlilik Bitişi', date: true },
];

const normalizeExcelDate = (value: unknown) => {
  if (typeof value === 'number' && value > 20000 && value < 100000) {
    const parsed = XLSX.SSF.parse_date_code(value);
    if (parsed) return `${parsed.y}-${String(parsed.m).padStart(2, '0')}-${String(parsed.d).padStart(2, '0')}`;
  }
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value.toISOString().slice(0, 10);
  return String(value || '').trim();
};

const normalizeExcelHeader = (value: unknown) => String(value || '')
  .trim()
  .toLocaleLowerCase('tr-TR')
  .replace(/ı/g, 'i')
  .replace(/ğ/g, 'g')
  .replace(/ü/g, 'u')
  .replace(/ş/g, 's')
  .replace(/ö/g, 'o')
  .replace(/ç/g, 'c')
  .replace(/\s+/g, ' ');

const getExcelColumnValue = (row: Record<string, unknown>, aliases: string[]) => {
  const normalizedAliases = new Set(aliases.map(normalizeExcelHeader));
  const matchedKey = Object.keys(row).find((key) => normalizedAliases.has(normalizeExcelHeader(key)));
  return matchedKey ? row[matchedKey] : undefined;
};

export function BasePrices() {
  const [rows, setRows] = useState<BasePriceRow[]>([]);
  const [warehouses, setWarehouses] = useState<Array<{ id: string; name: string }>>([]);
  const [priorityImportLoading, setPriorityImportLoading] = useState(false);
  const [backfillLoading, setBackfillLoading] = useState(false);
  const [importPreviewRows, setImportPreviewRows] = useState<any[]>([]);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importResult, setImportResult] = useState<{ imported: number; skipped: Array<{ row: any; reason: string }> } | null>(null);
  const [importSubmitting, setImportSubmitting] = useState(false);
  const [importError, setImportError] = useState('');
  const [importFeedback, setImportFeedback] = useState<{ tone: 'success' | 'error'; message: string } | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createSubmitting, setCreateSubmitting] = useState(false);
  const [createForm, setCreateForm] = useState<BasePriceRow>({ warehouse_id: '', warehouse_name: '', delivery_city: '', delivery_district: '', base_price: 0, own_assignment_priority: 0, valid_from: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<keyof BasePriceRow | ''>('');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [filterRules, setFilterRules] = useState<BasePriceFilterRule[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  const [showGrouping, setShowGrouping] = useState(false);
  const [groupBy, setGroupBy] = useState('');
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [editingRowId, setEditingRowId] = useState<string | null>(null);

  const columns: Array<{ key: keyof BasePriceRow; label: string }> = [
    { key: 'warehouse_name', label: 'Depo' },
    { key: 'delivery_city', label: 'Teslim İl' },
    { key: 'delivery_district', label: 'Teslim İlçe' },
    { key: 'base_price', label: 'Baz Fiyat' },
    { key: 'own_assignment_priority', label: 'Özmal Atama Önceliği' },
    { key: 'valid_from', label: 'Geçerlilik Tarihi' },
    { key: 'valid_until', label: 'Geçerlilik Bitişi' },
  ];

  const normalizeCompareText = (value: string) => String(value || '')
    .trim()
    .toLocaleLowerCase('tr-TR')
    .replace(/ı/g, 'i')
    .replace(/ğ/g, 'g')
    .replace(/ü/g, 'u')
    .replace(/ş/g, 's')
    .replace(/ö/g, 'o')
    .replace(/ç/g, 'c');

  const findWarehouseByName = (name: string) => {
    const target = normalizeCompareText(name);
    return warehouses.find((warehouse) => {
      const current = normalizeCompareText(warehouse.name);
      return current === target || current.includes(target) || target.includes(current);
    });
  };

  const loadData = async () => {
    const [pricesRes, warehousesRes] = await Promise.all([
      basePriceService.getAll(),
      warehouseService.getWarehouses(),
    ]);
    setRows((pricesRes.data || pricesRes || []).map((item: any) => ({
        id: String(item.id),
        warehouse_id: String(item.warehouse_id),
        warehouse_name: item.warehouse_name,
        delivery_city: item.delivery_city,
        delivery_district: item.delivery_district,
        base_price: Number(item.base_price || 0),
        own_assignment_priority: Number(item.own_assignment_priority || 0),
        valid_from: item.valid_from_str || (item.valid_from ? String(item.valid_from).slice(0, 10) : ''),
        valid_until: item.valid_until_str || (item.valid_until ? String(item.valid_until).slice(0, 10) : ''),
      })));
    setWarehouses((warehousesRes.data || warehousesRes || []).map((item: any) => ({ id: String(item.id), name: item.name })));
  };

  useEffect(() => {
    loadData();
  }, []);

  const filteredRows = useMemo(() => rows.filter((row) => {
    const matchesSearch = !searchTerm.trim() || [row.warehouse_name, row.delivery_city, row.delivery_district, String(row.base_price), String(row.own_assignment_priority), String(row.valid_from || ''), String(row.valid_until || '')].join(' ').toLocaleLowerCase('tr-TR').includes(searchTerm.trim().toLocaleLowerCase('tr-TR'));
    const evaluateRule = (rule: BasePriceFilterRule) => {
      const rawValue = String(row[rule.column] ?? '');
      const isDate = rule.column === 'valid_from' || rule.column === 'valid_until';
      if (isDate) {
        const date = rawValue.slice(0, 10);
        const today = new Date().toISOString().slice(0, 10);
        if (rule.operator === 'dateToday') return date === today;
        if (rule.operator === 'dateThisMonth') return date.slice(0, 7) === today.slice(0, 7);
        if (rule.operator === 'dateRange') {
          const [from, to] = rule.value.split(',');
          return Boolean(date) && (!from || date >= from) && (!to || date <= to);
        }
        if (rule.operator === 'dateEquals') return !rule.value || date === rule.value;
        if (rule.operator === 'dateOnOrAfter') return !rule.value || (Boolean(date) && date >= rule.value);
        if (rule.operator === 'dateOnOrBefore') return !rule.value || (Boolean(date) && date <= rule.value);
      }
      const cell = normalizeCompareText(rawValue);
      const value = normalizeCompareText(rule.value);
      if (rule.operator === 'empty') return !cell || cell === '-';
      if (rule.operator === 'notEmpty') return Boolean(cell) && cell !== '-';
      if (rule.operator === 'equals') return !value || cell === value;
      if (rule.operator === 'notEquals') return !value || cell !== value;
      if (rule.operator === 'notContains') return !value || !cell.includes(value);
      return !value || cell.includes(value);
    };
    const activeRules = filterRules.filter((rule) => ['empty', 'notEmpty', 'dateToday', 'dateThisMonth'].includes(rule.operator) || rule.value.trim());
    if (!matchesSearch || activeRules.length === 0) return matchesSearch;
    return activeRules.slice(1).reduce((accepted, rule) => rule.join === 'or' ? accepted || evaluateRule(rule) : accepted && evaluateRule(rule), evaluateRule(activeRules[0]));
  }), [rows, searchTerm, filterRules]);

  const sortedRows = useMemo(() => {
    if (!sortField) return filteredRows;
    return [...filteredRows].sort((a, b) => {
      const aVal = String(a[sortField] ?? '');
      const bVal = String(b[sortField] ?? '');
      const comparison = aVal.localeCompare(bVal, 'tr');
      return sortOrder === 'asc' ? comparison : -comparison;
    });
  }, [filteredRows, sortField, sortOrder]);

  const paginatedRows = useMemo(() => sortedRows.slice((page - 1) * pageSize, page * pageSize), [sortedRows, page, pageSize]);

  const groupedRows = useMemo(() => {
    if (!groupBy) return { all: paginatedRows };
    return paginatedRows.reduce((acc: Record<string, BasePriceRow[]>, item) => {
      const key = String(item[groupBy as keyof BasePriceRow] || '-');
      if (!acc[key]) acc[key] = [];
      acc[key].push(item);
      return acc;
    }, {});
  }, [paginatedRows, groupBy]);

  const showingAllRows = filteredRows.length > 100 && pageSize >= filteredRows.length;

  const handleExportExcel = () => exportRowsToExcel(sortedRows as any[], `base-prices-${new Date().toISOString().slice(0, 10)}.xlsx`, { sheetName: 'BasePrices' });

  const handleSort = (field: keyof BasePriceRow) => {
    if (sortField === field) {
      setSortOrder((current) => current === 'asc' ? 'desc' : 'asc');
      return;
    }
    setSortField(field);
    setSortOrder('asc');
  };

  const clearFilters = () => {
    setFilterRules([]);
    setSearchTerm('');
    setPage(1);
  };

  const activeFilterCount = filterRules.length;

  const addFilterRule = () => {
    setFilterRules((current) => [...current, {
      id: `base-price-filter-${Date.now()}-${Math.random().toString(16).slice(2)}`,
      join: 'and',
      column: 'warehouse_name',
      operator: 'contains',
      value: '',
    }]);
  };

  const updateFilterRule = (id: string, patch: Partial<BasePriceFilterRule>) => {
    setFilterRules((current) => current.map((rule) => rule.id === id ? { ...rule, ...patch } : rule));
    setPage(1);
  };

  const resetCreateForm = () => {
    setCreateForm({ warehouse_id: '', warehouse_name: '', delivery_city: '', delivery_district: '', base_price: 0, own_assignment_priority: 0, valid_from: '' });
  };

  const saveRow = async (row: BasePriceRow) => {
    const payload = {
      warehouseId: row.warehouse_id,
      deliveryCity: row.delivery_city,
      deliveryDistrict: row.delivery_district,
      basePrice: row.base_price,
      ownAssignmentPriority: row.own_assignment_priority,
      validFrom: row.valid_from,
    };
    if (row.id) {
      await basePriceService.update(row.id, payload);
    } else {
      await basePriceService.create(payload);
    }
    await loadData();
  };

  const deleteRow = async (row: BasePriceRow) => {
    if (!row.id) {
      setRows((current) => current.filter((item) => item !== row));
      return;
    }
    await basePriceService.delete(row.id);
    await loadData();
  };

  const togglePageSelection = () => {
    const pageIds = paginatedRows.map((row) => row.id).filter((id): id is string => Boolean(id));
    const pageIsSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.includes(id));
    setSelectedIds((current) => pageIsSelected
      ? current.filter((id) => !pageIds.includes(id))
      : [...new Set([...current, ...pageIds])]);
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`${selectedIds.length} baz fiyat kaydı silinecek. İlgili rota geçerlilikleri ve Genel Liste baz fiyatları yeniden hesaplanacak. Devam edilsin mi?`)) return;
    try {
      setBulkDeleting(true);
      const response: any = await basePriceService.bulkDelete(selectedIds);
      setSelectedIds([]);
      await loadData();
      setImportFeedback({ tone: 'success', message: response?.message || 'Seçili baz fiyatlar silindi.' });
    } catch (error: any) {
      console.error('Bulk delete base prices error:', error);
      setImportFeedback({ tone: 'error', message: error?.message || 'Seçili baz fiyatlar silinemedi.' });
    } finally {
      setBulkDeleting(false);
    }
  };

  const submitCreateForm = async () => {
    if (!createForm.warehouse_id || !createForm.delivery_city.trim() || !createForm.delivery_district.trim()) {
      window.alert('Depo, teslim il ve teslim ilçe zorunludur.');
      return;
    }

    try {
      setCreateSubmitting(true);
      await basePriceService.create({
        warehouseId: createForm.warehouse_id,
        deliveryCity: createForm.delivery_city,
        deliveryDistrict: createForm.delivery_district,
        basePrice: createForm.base_price,
        ownAssignmentPriority: createForm.own_assignment_priority,
        validFrom: createForm.valid_from,
      });
      setShowCreateModal(false);
      resetCreateForm();
      await loadData();
    } finally {
      setCreateSubmitting(false);
    }
  };

  const handleImport = async (file: File | null) => {
    if (!file) return;
    const buffer = await file.arrayBuffer();
    const workbook = XLSX.read(buffer, { type: 'array' });
    const sheet = workbook.Sheets[workbook.SheetNames[0]];
    const json = XLSX.utils.sheet_to_json(sheet);
    const preview = (json as any[]).map((row, index) => {
      const warehouseName = String(row.warehouseName || row.depo || row.DEPO || row.warehouse || row['Depo'] || '').trim();
      const deliveryCity = String(row.deliveryCity || row.teslimIl || row.TXIL || row['Teslim İl'] || row['TX IL'] || '').trim();
      const deliveryDistrict = String(row.deliveryDistrict || row.teslimIlce || row.TXILCE || row['Teslim İlçe'] || row['TX ILCE'] || '').trim();
      const basePriceRaw = row.basePrice ?? row.bazFiyat ?? row.DBLDEGER1 ?? row['Baz Fiyat'] ?? 0;
      const ownAssignmentPriorityRaw = row.ownAssignmentPriority ?? row.ozmalAtamaOnceligi ?? row['Özmal Atama Önceliği'] ?? row['Ozmal Atama Onceligi'] ?? 0;
      const validFromRaw = row.validFrom
        ?? row.valid_from
        ?? row.gecerlilikTarihi
        ?? getExcelColumnValue(row, ['Tarih', 'Geçerlilik Tarihi', 'Gecerlilik Tarihi', 'Valid From', 'ValidFrom'])
        ?? '';
      const validFrom = normalizeExcelDate(validFromRaw);
      const basePrice = Number(String(basePriceRaw).replace(/\./g, '').replace(',', '.'));
      const ownAssignmentPriority = Number(String(ownAssignmentPriorityRaw).replace(',', '.'));
      const warehouse = findWarehouseByName(warehouseName);
      const reason = !warehouseName || !deliveryCity || !deliveryDistrict
        ? 'Zorunlu alan eksik'
        : !warehouse
          ? `Depo bulunamadı: ${warehouseName}`
          : Number.isNaN(basePrice)
            ? 'Baz fiyat geçersiz'
            : '';

      return {
        line: index + 2,
        warehouseName,
        deliveryCity,
        deliveryDistrict,
        basePrice: Number.isNaN(basePrice) ? 0 : basePrice,
        ownAssignmentPriority: Number.isNaN(ownAssignmentPriority) ? 0 : ownAssignmentPriority,
        validFrom,
        matchedWarehouse: warehouse?.name || '',
        status: reason ? 'error' : 'ready',
        reason,
      };
    });
    setImportPreviewRows(preview);
    setImportResult(null);
    setImportError('');
    setImportFeedback(null);
    setShowImportModal(true);
  };

  const submitImport = async () => {
    const readyRows = importPreviewRows.filter((row) => row.status === 'ready').map((row) => ({
      warehouseName: row.warehouseName,
      deliveryCity: row.deliveryCity,
      deliveryDistrict: row.deliveryDistrict,
      basePrice: row.basePrice,
      ownAssignmentPriority: row.ownAssignmentPriority,
      validFrom: row.validFrom || undefined,
    }));
    if (readyRows.length === 0) return;

    try {
      setImportSubmitting(true);
      setImportError('');
      const response = await basePriceService.bulkImport(readyRows);
      const result = response.data || { imported: 0, skipped: [] };
      const skippedCount = Array.isArray(result.skipped) ? result.skipped.length : 0;
      setImportResult(result);
      setShowImportModal(false);
      setImportPreviewRows([]);
      setImportFeedback({
        tone: 'success',
        message: `${result.imported || 0} baz fiyat kaydı güncellendi.${skippedCount ? ` ${skippedCount} satır atlandı.` : ''}`,
      });
      try {
        await loadData();
      } catch (refreshError) {
        console.error('Base price list refresh error:', refreshError);
      }
    } catch (error: any) {
      console.error('Base price import error:', error);
      setImportError(error?.message || 'Baz fiyatlar içe aktarılamadı.');
    } finally {
      setImportSubmitting(false);
    }
  };

  const templateRows = useMemo(() => [
    { Depo: 'Merkez Depo', 'Teslim İl': 'İstanbul', 'Teslim İlçe': 'Tuzla', 'Baz Fiyat': 15000, 'Özmal Atama Önceliği': 10, 'Geçerlilik Tarihi': '2026-01-01' },
  ], []);

  const downloadTemplate = () => {
    const worksheet = XLSX.utils.json_to_sheet(templateRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Baz Fiyatlar');
    XLSX.writeFile(workbook, 'baz-fiyatlar-sablon.xlsx');
  };

  const handlePriorityImport = async (file: File | null) => {
    if (!file) return;
    try {
      setPriorityImportLoading(true);
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: 'array' });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const json = XLSX.utils.sheet_to_json(sheet);
      const mappedRows = (json as any[]).map((row, index) => ({
        line: index + 2,
        pickupCity: String(row.pickupCity || row.loadingPoint || row['Yükleme Noktası'] || row.yuklemeNoktasi || '').trim(),
        deliveryCity: String(row.deliveryCity || row.arrivalCity || row['Varış İli'] || row.varisIli || '').trim(),
        deliveryDistrict: String(row.deliveryDistrict || row.arrivalDistrict || row['Varış İlçesi'] || row.varisIlcesi || '').trim(),
        ownAssignmentPriority: row.ownAssignmentPriority ?? row['Özmal Atama Önceliği'] ?? row.ozmalAtamaOnceligi ?? row.priority ?? 0,
      }));

      if (mappedRows.length === 0) {
        window.alert('Excel dosyasında işlenecek satır bulunamadı.');
        return;
      }

      const response = await basePriceService.bulkUpdatePriorities(mappedRows);
      const result = response?.data || {};
      await loadData();
      const skipped = Array.isArray(result?.skipped) ? result.skipped.length : 0;
      window.alert(`${result?.updated || 0} baz fiyat önceliği güncellendi.${skipped > 0 ? ` ${skipped} satır eşleşmedi/atlandı.` : ''}`);
    } catch (error: any) {
      console.error('Priority import error:', error);
      window.alert(error?.message || 'Öncelik güncellemesi başarısız.');
    } finally {
      setPriorityImportLoading(false);
    }
  };

  const handleValidityBackfill = async () => {
    if (!window.confirm('Tüm baz fiyatların geçerlilik bitişleri ve ilgili Genel Liste baz fiyatları yeniden hesaplanacak. Devam edilsin mi?')) return;
    try {
      setBackfillLoading(true);
      const response: any = await basePriceService.backfillValidity();
      await loadData();
      window.alert(response?.message || 'Geçerlilik bitişleri güncellendi.');
    } catch (error: any) {
      console.error('Base price validity backfill error:', error);
      window.alert(error?.message || 'Geçerlilik bitişleri güncellenemedi.');
    } finally {
      setBackfillLoading(false);
    }
  };

  return (
    <div className="workiom-screen">
      <div className="workiom-toolbar">
        <div className="workiom-toolbar-left">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Baz Fiyatlar</h1>
            <p className="text-sm text-gray-600 mt-1">Depo, teslim il ve ilçe bazında baz fiyat tanımlayın</p>
          </div>
        </div>
        <div className="workiom-toolbar-right">
          <button onClick={handleExportExcel} className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
            <Download className="w-4 h-4" /> Excele Aktar
          </button>
           <button onClick={downloadTemplate} className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
             <Download className="w-4 h-4" /> Örnek Şablon İndir
           </button>
           <button onClick={() => void handleValidityBackfill()} disabled={backfillLoading} className="inline-flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2 text-sm font-medium text-amber-800 hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-60">
             <RotateCcw className={`w-4 h-4 ${backfillLoading ? 'animate-spin' : ''}`} /> {backfillLoading ? 'Hesaplanıyor...' : 'Geçerlilikleri Hesapla'}
           </button>
          <label className="inline-flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-4 py-2 text-sm font-medium text-blue-700 hover:bg-blue-100 cursor-pointer">
            <Upload className="w-4 h-4" /> Excel'den Toplu Yükle
            <input type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={(e) => handleImport(e.target.files?.[0] || null)} />
          </label>
          <label className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium cursor-pointer ${priorityImportLoading ? 'border border-violet-200 bg-violet-50 text-violet-400' : 'border border-violet-200 bg-violet-50 text-violet-700 hover:bg-violet-100'}`}>
            <Upload className="w-4 h-4" /> {priorityImportLoading ? 'Öncelikler Güncelleniyor...' : 'Öncelikleri Güncelle'}
            <input type="file" accept=".xlsx,.xls" className="hidden" disabled={priorityImportLoading} onChange={(e) => { void handlePriorityImport(e.target.files?.[0] || null); e.currentTarget.value = ''; }} />
          </label>
          <button
            onClick={() => {
              resetCreateForm();
              setShowCreateModal(true);
            }}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
          >
            <Plus className="w-4 h-4" /> Yeni Kayıt
          </button>
        </div>
      </div>

      {importFeedback ? (
        <div className={`mx-6 mt-4 flex items-center justify-between gap-3 rounded-lg border px-4 py-3 text-sm ${importFeedback.tone === 'success' ? 'border-emerald-200 bg-emerald-50 text-emerald-800' : 'border-rose-200 bg-rose-50 text-rose-800'}`}>
          <span>{importFeedback.message}</span>
          <button type="button" onClick={() => setImportFeedback(null)} className="font-medium">Kapat</button>
        </div>
      ) : null}

      <div className="mx-6 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
        Toplu yüklemede sütun başlıkları şu şekilde olabilir: <strong>Depo</strong>, <strong>Teslim İl</strong>, <strong>Teslim İlçe</strong>, <strong>Baz Fiyat</strong>, <strong>Özmal Atama Önceliği</strong>, <strong>Geçerlilik Tarihi</strong>. Depo adı, sistemde kayıtlı depo adı ile birebir eşleşmelidir.
        <div className="mt-2 text-amber-800">`Öncelikleri Güncelle` butonu `oncelik.xlsx` dosyasını okuyup <strong>Yükleme Noktası + Varış İli + Varış İlçesi</strong> alanlarını baz alır; baz fiyatları depo ili üzerinden eşleştirip sadece <strong>Özmal Atama Önceliği</strong> alanını günceller.</div>
      </div>

      <div className="workiom-toolbar !justify-start !pt-0">
        <div className="flex flex-wrap items-center gap-3 w-full px-6">
          <div className="workiom-search max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }} placeholder="Baz fiyat ara..." className="outline-none" />
          </div>
           <button onClick={() => setShowFilters((current) => !current)} className={`workiom-outline-button ${showFilters ? '!bg-blue-50 !border-blue-500 !text-blue-700' : ''}`}><Filter className="w-4 h-4" /> Filtrele</button>
           {activeFilterCount > 0 && <button onClick={clearFilters} className="workiom-outline-button"><XCircle className="w-4 h-4" /> Filtreleri Temizle</button>}
           {selectedIds.length > 0 && <button onClick={() => void handleBulkDelete()} disabled={bulkDeleting} className="inline-flex items-center gap-2 rounded-lg border border-rose-300 bg-rose-50 px-4 py-2 text-sm font-medium text-rose-700 hover:bg-rose-100 disabled:opacity-60"><Trash2 className="w-4 h-4" /> {bulkDeleting ? 'Siliniyor...' : `Seçilenleri Sil (${selectedIds.length})`}</button>}
          <div className="flex items-center gap-2">
            <button onClick={() => setShowGrouping((current) => !current)} className={`workiom-outline-button ${showGrouping ? '!bg-blue-50 !border-blue-500 !text-blue-700' : ''}`}><Filter className="w-4 h-4" /> Gruplandır</button>
            {showGrouping && <select value={groupBy} onChange={(e) => setGroupBy(e.target.value)} className="px-4 py-2 border border-gray-300 rounded-lg"><option value="">Gruplama Yok</option><option value="warehouse_name">Depo</option><option value="delivery_city">Teslim İl</option></select>}
          </div>
        </div>
      </div>

      {showFilters && (
        <div className="mx-6 mb-4 rounded-xl border border-slate-200 bg-slate-50 p-4">
          <div className="space-y-2">
            {filterRules.map((rule, index) => {
              const isDate = BASE_PRICE_FILTER_COLUMNS.find((column) => column.key === rule.column)?.date;
              const [rangeStart, rangeEnd] = rule.value.split(',');
              return (
                <div key={rule.id} className="flex flex-wrap items-center gap-2">
                  <select value={index === 0 ? 'and' : rule.join} onChange={(e) => updateFilterRule(rule.id, { join: e.target.value as 'and' | 'or' })} disabled={index === 0} className="h-9 rounded-lg border border-slate-300 bg-white px-2 text-sm disabled:opacity-60"><option value="and">VE</option><option value="or">VEYA</option></select>
                  <select value={rule.column} onChange={(e) => updateFilterRule(rule.id, { column: e.target.value as BasePriceFilterColumn, operator: BASE_PRICE_FILTER_COLUMNS.find((column) => column.key === e.target.value)?.date ? 'dateEquals' : 'contains', value: '' })} className="h-9 rounded-lg border border-slate-300 bg-white px-2 text-sm">{BASE_PRICE_FILTER_COLUMNS.map((column) => <option key={column.key} value={column.key}>{column.label}</option>)}</select>
                  <select value={rule.operator} onChange={(e) => updateFilterRule(rule.id, { operator: e.target.value as BasePriceFilterOperator, value: '' })} className="h-9 rounded-lg border border-slate-300 bg-white px-2 text-sm">
                    {isDate ? <><option value="dateEquals">Tarihe eşit</option><option value="dateOnOrAfter">Tarihinden sonra/eşit</option><option value="dateOnOrBefore">Tarihinden önce/eşit</option><option value="dateRange">Özel tarih aralığı</option><option value="dateToday">Bugün</option><option value="dateThisMonth">Bu ay</option><option value="empty">Boş</option><option value="notEmpty">Dolu</option></> : <><option value="contains">İçerir</option><option value="notContains">İçermez</option><option value="equals">Eşittir</option><option value="notEquals">Eşit değildir</option><option value="empty">Boş</option><option value="notEmpty">Dolu</option></>}
                  </select>
                  {rule.operator === 'dateRange' ? <><input type="date" value={rangeStart || ''} onChange={(e) => updateFilterRule(rule.id, { value: `${e.target.value},${rangeEnd || ''}` })} className="h-9 rounded-lg border border-slate-300 bg-white px-2 text-sm" /><span className="text-sm text-slate-500">ile</span><input type="date" value={rangeEnd || ''} onChange={(e) => updateFilterRule(rule.id, { value: `${rangeStart || ''},${e.target.value}` })} className="h-9 rounded-lg border border-slate-300 bg-white px-2 text-sm" /></> : <input type={isDate ? 'date' : 'text'} value={rule.value} onChange={(e) => updateFilterRule(rule.id, { value: e.target.value })} disabled={['empty', 'notEmpty', 'dateToday', 'dateThisMonth'].includes(rule.operator)} placeholder="Filtre değeri" className="h-9 min-w-48 rounded-lg border border-slate-300 bg-white px-2 text-sm disabled:opacity-60" />}
                  <button type="button" onClick={() => { setFilterRules((current) => current.filter((item) => item.id !== rule.id)); setPage(1); }} className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-rose-300 bg-rose-50 text-rose-700 hover:bg-rose-100"><X className="h-4 w-4" /></button>
                </div>
              );
            })}
          </div>
          <button type="button" onClick={addFilterRule} className="mt-3 inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100"><Plus className="h-4 w-4" /> Yeni Filtre Ekle</button>
        </div>
      )}

      <div className="workiom-grid-wrap mx-6 border border-gray-200 bg-white">
           <table className="workiom-grid-table text-sm">
            <thead>
              <tr>
                <th className="w-10 text-center"><input type="checkbox" checked={paginatedRows.length > 0 && paginatedRows.every((row) => row.id && selectedIds.includes(row.id))} onChange={togglePageSelection} aria-label="Sayfadaki kayıtları seç" /></th>
                {columns.map((column) => <th key={column.key} className="text-left cursor-pointer hover:bg-gray-100 select-none" onClick={() => handleSort(column.key)}><div className="flex items-center gap-2">{column.label}{sortField === column.key && (sortOrder === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />)}</div></th>)}
                <th className="text-left">İşlem</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(groupedRows).flatMap(([groupKey, groupItems]) => {
                const rendered: JSX.Element[] = [];
                if (groupBy) {
                  rendered.push(<tr key={`group-${groupKey}`} className="bg-gray-100"><td colSpan={columns.length + 2} className="px-3 py-2"><button type="button" onClick={() => setCollapsedGroups((current) => ({ ...current, [groupKey]: !current[groupKey] }))} className="flex items-center gap-2 text-sm font-medium text-gray-900">{collapsedGroups[groupKey] ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}<span>{groupKey}</span><span className="text-gray-500">({groupItems.length})</span></button></td></tr>);
                }
                if (!collapsedGroups[groupKey]) {
                  rendered.push(...groupItems.map((row, index) => {
                    const isEditing = !showingAllRows || editingRowId === row.id;
                    return (
                <tr key={row.id || `new-${index}`} style={showingAllRows ? { contentVisibility: 'auto', containIntrinsicSize: '48px' } : undefined}>
                  <td className="text-center"><input type="checkbox" checked={Boolean(row.id && selectedIds.includes(row.id))} onChange={() => row.id && setSelectedIds((current) => current.includes(row.id!) ? current.filter((id) => id !== row.id) : [...current, row.id])} aria-label={`${row.warehouse_name} kaydını seç`} /></td>
                  <td>
                    {isEditing ? <select
                      value={row.warehouse_id}
                      onChange={(e) => {
                        const warehouse = warehouses.find((item) => item.id === e.target.value);
                        setRows((current) => current.map((item) => item === row ? { ...item, warehouse_id: e.target.value, warehouse_name: warehouse?.name || '' } : item));
                      }}
                      className="w-full border rounded-lg px-3 py-2"
                    >
                      <option value="">Seçin</option>
                      {warehouses.map((warehouse) => <option key={warehouse.id} value={warehouse.id}>{warehouse.name}</option>)}
                    </select> : <span>{row.warehouse_name || '-'}</span>}
                  </td>
                  <td>{isEditing ? <input value={row.delivery_city} onChange={(e) => setRows((current) => current.map((item) => item === row ? { ...item, delivery_city: e.target.value } : item))} className="w-full border rounded-lg px-3 py-2" /> : row.delivery_city}</td>
                  <td>{isEditing ? <input value={row.delivery_district} onChange={(e) => setRows((current) => current.map((item) => item === row ? { ...item, delivery_district: e.target.value } : item))} className="w-full border rounded-lg px-3 py-2" /> : row.delivery_district}</td>
                  <td>{isEditing ? <input type="number" step="0.01" value={row.base_price} onChange={(e) => setRows((current) => current.map((item) => item === row ? { ...item, base_price: Number(e.target.value) } : item))} className="w-full border rounded-lg px-3 py-2" /> : Number(row.base_price || 0).toLocaleString('tr-TR')}</td>
                  <td>{isEditing ? <input type="number" step="1" value={row.own_assignment_priority} onChange={(e) => setRows((current) => current.map((item) => item === row ? { ...item, own_assignment_priority: Number(e.target.value || 0) } : item))} className="w-full border rounded-lg px-3 py-2" /> : row.own_assignment_priority}</td>
                   <td>{isEditing ? <input type="date" value={row.valid_from} onChange={(e) => setRows((current) => current.map((item) => item === row ? { ...item, valid_from: e.target.value } : item))} className="w-full border rounded-lg px-3 py-2" /> : row.valid_from}</td>
                   <td className="px-3 py-2 text-sm text-gray-600">{row.valid_until || '-'}</td>
                   <td>
                    <div className="flex items-center gap-2">
                      {isEditing ? <button onClick={async () => { await saveRow(row); setEditingRowId(null); }} className="inline-flex items-center gap-1 rounded-lg bg-emerald-600 px-3 py-2 text-white text-sm"><Save className="w-4 h-4" /> Kaydet</button> : <button onClick={() => setEditingRowId(row.id || null)} className="inline-flex items-center gap-1 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700">Düzenle</button>}
                      <button onClick={() => deleteRow(row)} className="inline-flex items-center gap-1 rounded-lg bg-rose-600 px-3 py-2 text-white text-sm"><Trash2 className="w-4 h-4" /> Sil</button>
                    </div>
                  </td>
                </tr>
                    );
                  }));
                }
                return rendered;
              })}
            </tbody>
           </table>
      </div>
      <div className="mx-6 mb-6 flex items-center justify-end gap-3">
        <WorkiomTablePagination page={page} pageSize={pageSize} totalRows={filteredRows.length} showAllOption onPageChange={setPage} onPageSizeChange={(size) => { setPageSize(size); setPage(1); }} />
      </div>

      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-2xl rounded-2xl bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Yeni Baz Fiyat</h2>
                <p className="mt-1 text-sm text-gray-600">Yeni baz fiyat kaydını modal form üzerinden oluşturun.</p>
              </div>
              <button onClick={() => setShowCreateModal(false)} className="rounded-lg p-2 hover:bg-gray-100"><X className="w-5 h-5" /></button>
            </div>
            <div className="grid grid-cols-1 gap-4 px-6 py-6 md:grid-cols-2">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Depo</label>
                <select
                  value={createForm.warehouse_id}
                  onChange={(e) => {
                    const warehouse = warehouses.find((item) => item.id === e.target.value);
                    setCreateForm((current) => ({ ...current, warehouse_id: e.target.value, warehouse_name: warehouse?.name || '' }));
                  }}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2"
                >
                  <option value="">Seçin</option>
                  {warehouses.map((warehouse) => <option key={warehouse.id} value={warehouse.id}>{warehouse.name}</option>)}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Teslim İl</label>
                <input value={createForm.delivery_city} onChange={(e) => setCreateForm((current) => ({ ...current, delivery_city: e.target.value }))} className="w-full rounded-lg border border-gray-300 px-3 py-2" />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Teslim İlçe</label>
                <input value={createForm.delivery_district} onChange={(e) => setCreateForm((current) => ({ ...current, delivery_district: e.target.value }))} className="w-full rounded-lg border border-gray-300 px-3 py-2" />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Baz Fiyat</label>
                <input type="number" step="0.01" value={createForm.base_price} onChange={(e) => setCreateForm((current) => ({ ...current, base_price: Number(e.target.value || 0) }))} className="w-full rounded-lg border border-gray-300 px-3 py-2" />
              </div>
              <div className="md:col-span-2">
                <label className="mb-1 block text-sm font-medium text-gray-700">Özmal Atama Önceliği</label>
                <input type="number" step="1" value={createForm.own_assignment_priority} onChange={(e) => setCreateForm((current) => ({ ...current, own_assignment_priority: Number(e.target.value || 0) }))} className="w-full rounded-lg border border-gray-300 px-3 py-2" />
              </div>
              <div className="md:col-span-2">
                <label className="mb-1 block text-sm font-medium text-gray-700">Geçerlilik Tarihi (boş bırakılırsa bugün)</label>
                <input type="date" value={createForm.valid_from} onChange={(e) => setCreateForm((current) => ({ ...current, valid_from: e.target.value }))} className="w-full rounded-lg border border-gray-300 px-3 py-2" />
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4">
              <button onClick={() => setShowCreateModal(false)} className="rounded-lg border border-gray-300 px-4 py-2 text-gray-700 hover:bg-gray-50">İptal</button>
              <button onClick={() => void submitCreateForm()} disabled={createSubmitting} className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 disabled:opacity-50">
                <Save className="w-4 h-4" /> {createSubmitting ? 'Kaydediliyor...' : 'Kaydet'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showImportModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Toplu Yükleme Önizleme</h2>
                <p className="text-sm text-gray-600 mt-1">Aktarılmadan önce satırları kontrol edin.</p>
              </div>
              <button onClick={() => setShowImportModal(false)} className="p-2 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
            </div>

            <div className="p-6 space-y-4 overflow-y-auto">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-4">
                  <div className="text-sm text-emerald-700">Hazır Satır</div>
                  <div className="text-2xl font-bold text-emerald-900 mt-1">{importPreviewRows.filter((row) => row.status === 'ready').length}</div>
                </div>
                <div className="rounded-xl bg-rose-50 border border-rose-200 p-4">
                  <div className="text-sm text-rose-700">Atlanacak Satır</div>
                  <div className="text-2xl font-bold text-rose-900 mt-1">{importPreviewRows.filter((row) => row.status === 'error').length}</div>
                </div>
                <div className="rounded-xl bg-blue-50 border border-blue-200 p-4">
                  <div className="text-sm text-blue-700">Toplam Satır</div>
                  <div className="text-2xl font-bold text-blue-900 mt-1">{importPreviewRows.length}</div>
                </div>
              </div>

              <div className="border border-gray-200 rounded-xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-4 py-3 text-left">Satır</th>
                        <th className="px-4 py-3 text-left">Excel Depo</th>
                        <th className="px-4 py-3 text-left">Eşleşen Depo</th>
                        <th className="px-4 py-3 text-left">Teslim İl</th>
                        <th className="px-4 py-3 text-left">Teslim İlçe</th>
                        <th className="px-4 py-3 text-left">Geçerlilik Tarihi</th>
                        <th className="px-4 py-3 text-left">Baz Fiyat</th>
                        <th className="px-4 py-3 text-left">Durum</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {importPreviewRows.map((row) => (
                        <tr key={`${row.line}-${row.warehouseName}-${row.deliveryCity}`} className={row.status === 'error' ? 'bg-rose-50' : ''}>
                          <td className="px-4 py-3">{row.line}</td>
                          <td className="px-4 py-3">{row.warehouseName || '-'}</td>
                          <td className="px-4 py-3">{row.matchedWarehouse || '-'}</td>
                          <td className="px-4 py-3">{row.deliveryCity || '-'}</td>
                          <td className="px-4 py-3">{row.deliveryDistrict || '-'}</td>
                          <td className="px-4 py-3">{row.validFrom ? String(row.validFrom).slice(0, 10) : '-'}</td>
                          <td className="px-4 py-3">{Number(row.basePrice || 0).toLocaleString('tr-TR')} ₺</td>
                          <td className="px-4 py-3">
                            {row.status === 'ready' ? (
                              <span className="inline-flex items-center gap-1 text-emerald-700 font-medium">Hazır</span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-rose-700 font-medium"><AlertTriangle className="w-4 h-4" /> {row.reason}</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {importResult?.skipped?.length ? (
                <div className="rounded-xl border border-rose-200 bg-rose-50 p-4">
                  <h3 className="font-semibold text-rose-900 mb-3">Atlanan Satırlar</h3>
                  <div className="space-y-2 text-sm text-rose-800 max-h-48 overflow-y-auto">
                    {importResult.skipped.map((item, index) => (
                      <div key={`${item.reason}-${index}`} className="border-b border-rose-100 pb-2 last:border-b-0">
                        <div>{item.reason}</div>
                        <div className="text-xs text-rose-700 mt-1">{JSON.stringify(item.row)}</div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
              {importError ? (
                <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">
                  {importError}
                </div>
              ) : null}
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between gap-3">
              <div className="text-sm text-gray-500">
                Hazır kayıtlar içe aktarılacak, hatalı satırlar atlanacaktır.
              </div>
              <div className="flex items-center gap-3">
                <button onClick={() => setShowImportModal(false)} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">Kapat</button>
                <button
                  onClick={submitImport}
                  disabled={importSubmitting || importPreviewRows.filter((row) => row.status === 'ready').length === 0}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {importSubmitting ? <><Loader2 className="mr-2 inline h-4 w-4 animate-spin" />İçe Aktarılıyor...</> : 'Hazır Satırları İçe Aktar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
